/**
 * Utility functions for PowerPoint export to prevent errors
 */

/**
 * Sanitizes text for PowerPoint by removing invalid characters and normalizing whitespace
 * Also removes Word metadata patterns that can leak into content
 */
export function sanitizeTextForPowerPoint(text: string | null | undefined): string {
  if (!text) return '';
  
  // Convert to string
  let sanitized = String(text);
  
  // Remove Word metadata patterns that can leak into content
  // Pattern: "Normal\n0\n21\nfalse\nfalse\nfalse\nFR\nX-NONE\nX-NONE"
  sanitized = sanitized.replace(/Normal\s+\d+\s+\d+\s+false\s+false\s+false\s+[A-Z]{2}\s+X-NONE\s+X-NONE/gi, '');
  
  // Remove individual metadata lines that might appear separately
  sanitized = sanitized.replace(/^\s*Normal\s*$/gmi, ''); // Remove lines that are just "Normal"
  sanitized = sanitized.replace(/^\s*\d+\s*$/gm, ''); // Remove lines that are just numbers (likely metadata)
  sanitized = sanitized.replace(/^\s*false\s*$/gmi, ''); // Remove lines that are just "false"
  sanitized = sanitized.replace(/^\s*true\s*$/gmi, ''); // Remove lines that are just "true"
  sanitized = sanitized.replace(/^\s*FR\s*$/gmi, ''); // Remove lines that are just "FR" (language code)
  sanitized = sanitized.replace(/^\s*EN\s*$/gmi, ''); // Remove lines that are just "EN" (language code)
  sanitized = sanitized.replace(/^\s*X-NONE\s*$/gmi, ''); // Remove lines that are just "X-NONE"
  
  // Remove null bytes and other control characters (except newlines and tabs)
  // eslint-disable-next-line no-control-regex
  sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');
  
  // Normalize line breaks
  sanitized = sanitized.replace(/\r\n/g, '\n');
  sanitized = sanitized.replace(/\r/g, '\n');
  
  // Remove empty lines (that might have been created by removing metadata)
  sanitized = sanitized.replace(/\n\s*\n\s*\n/g, '\n\n');
  
  // Replace multiple consecutive newlines with maximum of 2
  sanitized = sanitized.replace(/\n{3,}/g, '\n\n');
  
  // Trim whitespace
  sanitized = sanitized.trim();
  
  return sanitized;
}

/**
 * Escapes special characters that might cause issues in PowerPoint
 */
export function escapePowerPointText(text: string): string {
  if (!text) return '';
  
  // Replace problematic characters
  // eslint-disable-next-line no-control-regex
  let escaped = text
    // eslint-disable-next-line no-control-regex
    .replace(/\x00/g, '') // Remove null bytes
    // eslint-disable-next-line no-control-regex
    .replace(/[\x01-\x08\x0B-\x0C\x0E-\x1F]/g, ''); // Remove control chars
  
  return escaped;
}

/**
 * Validates and sanitizes color hex code for PowerPoint
 */
export function sanitizeColor(color: string | undefined | null): string {
  if (!color) return '000000'; // Default to black
  
  // Remove # if present
  let sanitized = color.replace(/^#/, '');
  
  // Ensure it's a valid hex color (6 characters)
  if (!/^[0-9A-Fa-f]{6}$/.test(sanitized)) {
    return '000000'; // Default to black if invalid
  }
  
  return sanitized.toUpperCase();
}

/**
 * Ensures text is not empty and returns a default if needed
 */
export function ensureNonEmptyText(text: string | null | undefined, defaultValue: string = ''): string {
  const sanitized = sanitizeTextForPowerPoint(text);
  return sanitized || defaultValue;
}

/**
 * Validates and sanitizes fontSize for PowerPoint
 */
export function sanitizeFontSize(size: number | undefined | null, defaultSize: number = 14): number {
  if (!size || size <= 0) return defaultSize;
  if (size > 400) return 400; // PowerPoint max is around 400
  if (size < 1) return 1;
  return Math.round(size);
}

/**
 * Validates position and size values for PowerPoint
 */
export function sanitizePosition(value: number | undefined | null, defaultValue: number = 0): number {
  if (value === undefined || value === null || isNaN(value)) return defaultValue;
  if (value < 0) return 0;
  if (value > 10) return 10; // PowerPoint slide width is 10 inches
  return Math.round(value * 100) / 100; // Round to 2 decimals
}
